import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AlertController, LoadingController } from '@ionic/angular';
import { ModalController, ToastController } from '@ionic/angular/standalone';

@Injectable({
    providedIn: 'root'
})
export class IonicService {
    LOADING: any;
    alert: any = [];
    private toastQueue: Promise<unknown> = Promise.resolve();
    private currentToast: any = null;

    constructor(
        private router: Router,
        private loadingController: LoadingController,
        private alertController: AlertController,
        private modalController: ModalController,
        private toastController: ToastController
    ) {}

    /**
     * Enhanced method to navigate to a page and show loading
     * @param page Route to navigate to
     * @param param Optional URL parameter
     * @param replace Replace URL in history
     */
    goPage(page: string, param?: string, replace: boolean = true) {
        const urlParam = !!param ? param : '';
        this.router.navigate([`${page}/${urlParam}`], { replaceUrl: replace });
    }

    /**
     * Create and present a modal
     */
    async showModal(
        modalPage: any,
        cssClass = [''],
        componentProps?: any,
        allowDismiss?: boolean
    ) {
        const modal = await this.modalController.create({
            component: modalPage,
            componentProps,
            cssClass,
            backdropDismiss: allowDismiss != null ? allowDismiss : true
        });
        await modal.present();
        return modal;
    }

    /**
     * Create and present an alert
     */
    async showAlert(
        header: string,
        message: string,
        buttons?: any[],
        inputs?: any[],
        id?: string
    ) {
        if (this.alert.length > 0) {
            this.alert.forEach(async (alert: any) => {
                await alert.dismiss();
            });
            this.alert = [];
        }
        const alert = await this.alertController.create({
            mode: 'ios',
            header,
            message,
            id: id ?? 'alert',
        });
        this.alert.push(alert);

        if (Boolean(buttons?.length) && buttons) {
            alert.buttons = buttons;
        }

        if (Boolean(inputs?.length) && inputs) {
            alert.inputs = inputs;
        }

        await alert.present();

        return alert.onDidDismiss();
    }

    /**
     * Dismiss the active modal
     */
    dismissModal(data?: unknown) {
        this.modalController.dismiss(data ?? null);
    }

    /**
     * Dismiss the active alert
     */
    dismissAlert() {
        this.alertController.dismiss();
    }

    /**
     * Show a loading indicator
     */
    async showLoading(message?: string, duration?: number) {
        this.LOADING = await this.loadingController.create({
            message: message || 'Loading',
            spinner: 'bubbles',
        });

        if (Boolean(duration)) {
            this.LOADING.duration = duration;
        }

        await this.LOADING.present();
    }

    /**
     * Dismiss the active loading indicator
     */
    async dismissLoading() {
        if (this.LOADING) {
            await this.LOADING.dismiss();
        }
    }
    
    /**
     * Show a toast message with specified type and duration
     */
    async showToast(message: string, type: 'success' | 'info' | 'warning' | 'error', duration = 3000) {
        // Ionic no apila los toasts: cada uno es un overlay independiente en la misma
        // posicion, asi que dos simultaneos quedan superpuestos y los textos se enciman.
        // Las llamadas se encolan para que siempre quede visible uno solo, incluso
        // cuando se disparan al mismo tiempo desde distintos puntos del mismo flujo.
        this.toastQueue = this.toastQueue
            .catch(() => undefined)
            .then(() => this.presentToast(message, type, duration));

        return this.toastQueue;
    }

    /**
     * Dismiss the visible toast, if any, and present the new one
     */
    private async presentToast(
        message: string,
        type: 'success' | 'info' | 'warning' | 'error',
        duration: number
    ) {
        if (this.currentToast) {
            const previousToast = this.currentToast;
            this.currentToast = null;
            await previousToast.dismiss().catch(() => undefined);
        }

        const toast = await this.toastController.create({
            position: 'top',
            message,
            duration,
            cssClass: ['toast-item', `toast-${type}`],
            icon: type === 'success' ? 'assets/icon/toast-success.svg' : 'assets/icon/toast-error.svg',
            color: 'unset',
        });

        this.currentToast = toast;
        toast.onDidDismiss().then(() => {
            if (this.currentToast === toast) {
                this.currentToast = null;
            }
        });

        await toast.present();
    }
}
